import React, { useState, useMemo } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InputGroup } from '../common/InputGroup';
import { DropdownMultiTypeahead } from '../common/DropdownMultiTypeahead';
import { Button } from '../common/Button';
import { useProfileStore } from '../../stores/profileStore';
import { toast } from 'react-toastify';
import { useIndustrySelector } from '../../hooks/useIndustrySelector';
import { COMPANY_TYPES } from '../../types/profile';
import { ProfileAvatarUpload } from './ProfileAvatarUpload';
import FormLanguageSelector from '../common/FormLanguageSelector';
import { DropdownTypeahead } from '../common/DropdownTypeahead';
import { City } from '../../types/profile';
import { useCitySelector } from '../../hooks/useCitySelector';
import { Industry } from '../../stores/industryStore';

interface EditCompanyInfoProps {
  onSave?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
}

const EditCompanyInfo: React.FC<EditCompanyInfoProps> = ({
  onSave,
  onNext,
  onPrevious,
  onSkip,
}) => {
  const { t } = useTranslation(['common', 'settings', 'auth']);
  const { profile, completeProfile, suggestNewIndustry } = useProfileStore();

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{
    companyName?: string;
    industries?: string;
    jobTitle?: string;
    organizationIdentity?: string;
    city?: string;
    languages?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    postalCode?: string;
  }>({});

  const [companyName, setCompanyName] = useState(profile?.company_name);
  const [jobTitle, setJobTitle] = useState(profile?.job_title);
  const [organizationWebsite, setOrganizationWebsite] = useState(
    profile?.organization_website || ''
  );
  const [organizationNumber, setOrganizationNumber] = useState(profile?.organization_number || '');
  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');

  const [phone, setPhone] = useState(profile?.phone || '');
  const [languages, setLanguages] = useState<string[]>(profile?.languages || []);

  const [imageUrl, setImageUrl] = useState(profile?.avatar_url || null);
  const [postalCode, setPostalCode] = useState(profile?.postal_code || '');

  const [selectedCities, setSelectedCities] = useState<City[]>(profile?.city ? [profile.city] : []);

  const { options: cityOptions } = useCitySelector(selectedCities);

  const {
    selectedOptions: selectedIndustries,
    setSelectedOptions: setSelectedIndustries,
    filteredOptions: industryOptions,
    setSearchInput: setIndustrySearchInput,
  } = useIndustrySelector(profile?.industries || []);

  // Track if the selection has changed
  const initialIndustryIds = useMemo(
    () =>
      (profile?.industries || [])
        .map(i => String(i.id))
        .sort()
        .join(','),
    [profile?.industries]
  );
  const currentIndustryIds = useMemo(
    () =>
      selectedIndustries
        .map(i => String(i.id))
        .sort()
        .join(','),
    [selectedIndustries]
  );

  const selectedCity = selectedCities[0];

  const isEdited = useMemo(() => {
    return (
      (profile?.first_name || '') !== firstName ||
      (profile?.last_name || '') !== lastName ||
      (profile?.phone || '') !== phone ||
      (profile?.languages?.join(',') || '') !== languages.join(',') ||
      (profile?.city?.id || null) !== (selectedCity?.id || null) ||
      (profile?.company_name || '') !== (companyName || '') ||
      (profile?.job_title || '') !== (jobTitle || '') ||
      (profile?.organization_website || '') !== organizationWebsite ||
      (profile?.organization_number || '') !== organizationNumber ||
      initialIndustryIds !== currentIndustryIds ||
      (profile?.postal_code || '') !== (postalCode || '')
    );
  }, [
    firstName,
    lastName,
    phone,

    languages,
    selectedCity,
    companyName,
    jobTitle,
    organizationWebsite,
    organizationNumber,
    currentIndustryIds,
    initialIndustryIds,
    postalCode,
    profile,
  ]);
  // Validation handler
  const validate = () => {
    const newErrors: typeof errors = {};

    // Validation des informations personnelles
    if (!firstName.trim()) newErrors.firstName = t('error.fieldRequired', { ns: 'common' });
    if (!lastName.trim()) newErrors.lastName = t('error.fieldRequired', { ns: 'common' });
    if (!selectedCity) newErrors.city = t('error.fieldRequired', { ns: 'common' });
    if (!phone.trim()) newErrors.phone = t('error.fieldRequired', { ns: 'common' });
    if (!languages.length) newErrors.languages = t('error.fieldRequired', { ns: 'common' });

    // Validation des informations entreprise
    if (
      profile?.company_type === COMPANY_TYPES.COMPANY ||
      profile?.company_type === COMPANY_TYPES.ORGANIZATION
    ) {
      if (!companyName?.trim()) newErrors.companyName = t('error.fieldRequired', { ns: 'common' });
    }
    if (profile?.company_type === COMPANY_TYPES.ORGANIZATION) {
      if (!jobTitle?.trim()) newErrors.jobTitle = t('error.fieldRequired', { ns: 'common' });
      if (!organizationWebsite.trim() && !organizationNumber.trim()) {
        newErrors.organizationIdentity = t('organizationIdentityRequired', { ns: 'auth' });
      }
    }
    if (profile?.company_type !== COMPANY_TYPES.INDIVIDUAL) {
      if (!selectedIndustries.length)
        newErrors.industries = t('error.fieldRequired', { ns: 'common' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate() || !profile?.id) return;

    setSaving(true);
    try {
      await completeProfile({
        userId: profile.id,
        firstName,
        lastName,
        phone,
        jobTitle,
        postalCode,
        bio: profile?.bio || '',
        cityId: selectedCity?.id || null,
        languages,
        companyName,
        organizationWebsite,
        organizationNumber,
        industries: selectedIndustries.map(i => i.id),
      });

      toast.success(t('profileUpdateSuccess', { ns: 'settings' }));
      if (onNext) {
        onNext();
      } else if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error(err);
      toast.error(t('profileUpdateError', { ns: 'settings' }));
    } finally {
      setSaving(false);
    }
  };

  // client-side search handled by useIndustrySelector

  const handleIndustryAdded = (industry: Industry) => {
    setSelectedIndustries([...selectedIndustries, industry]);
  };

  const handleCreateNewIndustry = async (query: string) => {
    try {
      const newIndustry = await suggestNewIndustry({
        label: query,
        description: '',
      });

      handleIndustryAdded(newIndustry);

      toast.success(t('industryAddedSuccess', { ns: 'settings', defaultValue: 'Industry added!' }));
    } catch (err) {
      console.error('Error creating industry:', err);
      toast.error(
        t('industryAddError', { ns: 'settings', defaultValue: 'Could not add industry.' })
      );
    }
  };

  return (
    <form className="max-w-screen-lg" onSubmit={handleSave}>
      <div className="mt-8 flex flex-col gap-12 lg:flex-row">
        <div className="flex w-full justify-center lg:w-1/3 lg:justify-start">
          <ProfileAvatarUpload
            profileId={profile?.id || ''}
            imageUrl={imageUrl}
            size={192}
            design="settings"
            onChange={setImageUrl}
          />
        </div>
        <div className="grid w-full grid-cols-1 gap-x-8 gap-y-3 md:grid-cols-2">
          {profile?.company_type !== COMPANY_TYPES.INDIVIDUAL && (
            <>
              <InputGroup
                label={t('form_fields.companyName', { ns: 'common' })}
                value={companyName || ''}
                onChange={e => setCompanyName(e.target.value)}
                placeholder={t('form_fields.companyNamePlaceholder', { ns: 'common' })}
                required
                error={!!errors.companyName}
                errorMessage={errors.companyName}
              />
              <InputGroup
                label={t('form_fields.jobTitle', { ns: 'common' })}
                value={jobTitle || ''}
                onChange={e => setJobTitle(e.target.value)}
                placeholder={t('form_fields.jobTitlePlaceholder', { ns: 'common' })}
                required={profile?.company_type === COMPANY_TYPES.ORGANIZATION}
                error={!!errors.jobTitle}
                errorMessage={errors.jobTitle}
              />
            </>
          )}
          <InputGroup
            label={t('city', { ns: 'auth' })}
            required
            error={!!errors.city}
            errorMessage={errors.city}
          >
            <DropdownTypeahead
              className="mt-2"
              value={profile?.city?.name || ''}
              onChange={(_val: string, item?: City) => {
                setSelectedCities(item ? [item] : []);
              }}
              options={cityOptions}
              getOptionLabel={item => item.name}
              getOptionValue={item => item.id.toString()}
              placeholder={t('cityPlaceholder', { ns: 'auth' })}
              showDropdownIcon={true}
              error={!!errors.city}
            />
          </InputGroup>

          <InputGroup
            label={t('postalCode', { ns: 'auth' })}
            value={postalCode}
            onChange={e => setPostalCode(e.target.value)}
            placeholder={t('postalCodePlaceholder', { ns: 'auth' })}
            error={!!errors.postalCode}
            errorMessage={errors.postalCode}
          />

          <FormLanguageSelector
            required
            value={languages}
            error={errors.languages}
            onChange={setLanguages}
          />

          <div className="md:col-span-2">
            {profile?.company_type !== COMPANY_TYPES.INDIVIDUAL && (
              <>
                <div className="md:col-span-2">
                  <InputGroup
                    label={t('form_fields.businessIndustries', { ns: 'common' })}
                    required
                    error={!!errors.industries}
                    errorMessage={errors.industries}
                  >
                    <DropdownMultiTypeahead
                      selected={selectedIndustries}
                      onChange={items => {
                        setSelectedIndustries(items);
                        if (errors.industries)
                          setErrors(prev => ({ ...prev, industries: undefined }));
                      }}
                      onSearchChange={setIndustrySearchInput}
                      options={industryOptions}
                      getOptionLabel={item => item.label}
                      getOptionValue={item => item.id}
                      placeholder={t('form_fields.businessIndustriesHint', { ns: 'common' })}
                      className="mt-1"
                      error={!!errors.industries}
                      onCreate={handleCreateNewIndustry}
                    />
                  </InputGroup>
                </div>
              </>
            )}
          </div>

          <InputGroup
            label={t('contactFirstName', { ns: 'auth' })}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
            placeholder={t('firstNamePlaceholder')}
            required
            error={!!errors.firstName}
            errorMessage={errors.firstName}
          />

          <InputGroup
            label={t('contactLastName', { ns: 'auth' })}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            placeholder={t('lastNamePlaceholder')}
            required
            error={!!errors.lastName}
            errorMessage={errors.lastName}
          />

          {profile?.company_type === COMPANY_TYPES.ORGANIZATION && (
            <>
              <InputGroup
                label={t('organizationWebsite', { ns: 'auth' })}
                value={organizationWebsite}
                onChange={e => {
                  setOrganizationWebsite(e.target.value);
                  if (errors.organizationIdentity) {
                    setErrors(prev => ({ ...prev, organizationIdentity: undefined }));
                  }
                }}
                placeholder={t('organizationWebsitePlaceholder', { ns: 'auth' })}
                error={!!errors.organizationIdentity}
                errorMessage={errors.organizationIdentity}
              />
              <InputGroup
                label={t('organizationNumber', { ns: 'auth' })}
                value={organizationNumber}
                onChange={e => {
                  setOrganizationNumber(e.target.value);
                  if (errors.organizationIdentity) {
                    setErrors(prev => ({ ...prev, organizationIdentity: undefined }));
                  }
                }}
                placeholder={t('organizationNumberPlaceholder', { ns: 'auth' })}
                error={!!errors.organizationIdentity}
                errorMessage={errors.organizationIdentity}
              />
            </>
          )}

          <InputGroup
            label={t('phone', { ns: 'auth' })}
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder={t('phonePlaceholder', { ns: 'auth' })}
            required
            error={!!errors.phone}
            errorMessage={errors.phone}
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-4 md:flex-row-reverse">
        <Button
          variant="primary"
          type="submit"
          className="px-8 py-2"
          disabled={(!isEdited || saving) && !onSave && !onNext}
        >
          {saving
            ? t('common:form_actions.saving')
            : onNext
              ? t('common:form_actions.next')
              : t('common:form_actions.save')}
        </Button>
        {onSkip && (
          <Button
            variant="transparent-with-border"
            type="button"
            onClick={onSkip}
            className="px-8 py-2"
          >
            {t('common:form_actions.skip', { ns: 'common' })}
          </Button>
        )}
        {onPrevious && (
          <Button
            variant="simple-text"
            type="button"
            onClick={onPrevious}
            className="flex items-center gap-2 px-8 py-2"
          >
            <ChevronLeft size={20} aria-hidden />
            {t('common:form_actions.previous', { ns: 'common' })}
          </Button>
        )}
      </div>
    </form>
  );
};

export default EditCompanyInfo;
